import { createContext, useContext, useRef, useCallback, ReactNode } from "react";

interface AudioContextType {
  registerAudio: (id: string, audioElements: HTMLAudioElement[]) => void;
  unregisterAudio: (id: string) => void;
  stopAllAudio: () => void;
}

const AudioContext = createContext<AudioContextType | null>(null);

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error("useAudio must be used within an AudioProvider");
  }
  return context;
};

interface AudioProviderProps {
  children: ReactNode;
}

export const AudioProvider = ({ children }: AudioProviderProps) => {
  const audioRegistry = useRef<Map<string, HTMLAudioElement[]>>(new Map());

  const stopAllAudio = useCallback(() => {
    audioRegistry.current.forEach((audioElements) => {
      audioElements.forEach((audio) => {
        try {
          audio.pause();
          // Fully release the media resource so it can't keep playing after navigation
          audio.src = "";
          audio.load();
          audio.currentTime = 0;
        } catch {
          // no-op
        }
      });
    });
  }, []);

  const registerAudio = useCallback((id: string, audioElements: HTMLAudioElement[]) => {
    audioRegistry.current.set(id, audioElements);
  }, []);

  const unregisterAudio = useCallback((id: string) => {
    // IMPORTANT: Do not pause/clear audio here.
    // StoryPlayer re-registers audio elements as chunks load; cleaning up here would
    // interrupt active playback. Actual cleanup is handled by stopAllAudio() and
    // component unmount effects.
    audioRegistry.current.delete(id);
  }, []);

  return (
    <AudioContext.Provider value={{ registerAudio, unregisterAudio, stopAllAudio }}>
      {children}
    </AudioContext.Provider>
  );
};
